import { Dispatch, SetStateAction, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { NavigateOptions, To, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import styles from './MapScreen.module.css';
import Toast, { ToastData } from '../components/common/Toast';

//import Map from '../components/map/Map';
import ScaleBar from '../components/map/ScaleBar';

import { CategoryOptions, MapInfo, path, PlaceCoordsList } from '../components/common/Common';
import DirectionAnalysis, { Stage } from '../components/map/sidesheet/analysis/DirectionAnalysis';
import { useSlopeAnalysis } from '../hooks/useSlopeAnalysis';

import AccessibilityAnalysis from '../components/map/sidesheet/analysis/AccessibilityAnalysis';
import { TransportMode } from '../components/map/sidesheet/analysis/TransportSelector';
import DataProvider from '../data_provider/DataProvider';
import { Projection } from 'ol/proj';
import { Map as OlMap } from 'ol';
import { Polygon } from 'ol/geom';

import { POISearchOptions } from '../components/map/sidesheet/SearchPOI';
import LoadingCoverLayer from '../components/common/LoadingCoverLayer';

import { ContentList, MapToolType } from './MapScreen';
import { useWebViewBridge, WebViewMessageType } from '../hooks/useWebViewBridge';
import MobileMap from '../components/map/MobileMap';

interface MapScreenProps {
    setIsMap: Dispatch<SetStateAction<boolean>>;
}

export interface AccessibilityState {
    vehicle?: TransportMode;
    ready: boolean;
}

const defaultCategory: CategoryOptions = {
    label: '전체',
    disabled: false,
};

const MobileMapScreen = (props: MapScreenProps) => {
    //const [navigate, location] = [useNavigate(), useLocation()];
    const [navigate] = [useNavigate(), useLocation()];

    // MobileMapScreen에서 URL 파라미터 추출 시작
    const [params] = useSearchParams();
    // 기본 지도ID 추출 - 메인 지도 식별용
    const baseMapID = params.get('id');

    const [mapInfos, setMapInfos] = useState<MapInfo[]>([]); // 기본 지도(인덱스 0) + 오버랩 지도들을 통합 관리

    const [categoriesArray, setCategoriesArray] = useState<CategoryOptions[][]>([[{ label: '전체', disabled: false }]]); // 각 지도별 카테고리 2차원 배열

    // 25.09.02 | Kim Young Jun | selectedCategory 제거하고 selectedCategoryArray만 사용
    const prevSelectedCategoryRef = useRef<number>(0);

    // 검색 옵션 state
    const [searchOptions, setSearchOptions] = useState<POISearchOptions | undefined>({
        category: undefined,
        search: undefined,
        XID: undefined,
    });

    const [isLoading, setIsLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');

    // 신규 장소 추가 관련 state -----------------------------------
    const [newPlaceCoords, setNewPlaceCoords] = useState<PlaceCoordsList[]>([]); // index == fileIdx
    const [isAddMode, setIsAddMode] = useState(false);
    const [editPlaceStatus, setEditPlaceStatus] = useState<boolean>(false);
//    const prevSelectedIdxRef = useRef<number | null>(null); // [36]
    const [newPlaceAddress, setNewPlaceAddress] = useState<string>('');

    const activeIdxRef = useRef<MapToolType>(MapToolType.Init);
    const prevActiveIdxRef = useRef<MapToolType>(MapToolType.Init);

    const [activeIdx, _setActiveIdx] = useState(MapToolType.Init);
    const setActiveIdx = (next: MapToolType) => {
        // setState 호출 전에 prev를 스냅샷 - 같은 툴을 선택하면 reset을 위한 용도
        prevActiveIdxRef.current = activeIdxRef.current;
        activeIdxRef.current = next;
        _setActiveIdx(next);
    };

    const [isGPSOn, setIsGPSOn] = useState(false); // [40]
    const [zoomLv, setZoomLv] = useState<number>(0);

    // POI 관련 state -------------------------------------------
    const [selectedPoiIdx, setSelectedPoiIdx] = useState<number | string>(0); // xid가 1부터 시작하므로 선택되지 않은 상태 == 0

    // 계산·분석도구 관련 state ----------------------------------------------
    const [blur, setBlur] = useState<number>(15); // heatmap

    const [analysisXIDs, setAnalysisXIDs] = useState<number[] | undefined>(undefined); // 분석결과 XIDs (시간별 이동거리, 접근성 분석, 범위분석 공통) [45]
    const [clearByButton, setClearByButton] = useState<Stage>(-1); // 분석버튼 클릭 후 [이전 혹은 초기화]버튼으로 map source Clear 시 이용
    const [analysisStage, setAnalysisStage] = useState<Stage>(-1); // 분석 단계 기억

    const [isExtentAnalysis, setIsExtentAnalysis] = useState<boolean>(false); // 범위분석 활성화 여부

    const [selectedMapIdx, setSelectedMapIdx] = useState<number>(0); // 선택된 지도 인덱스 (0: 기준지도, 1+: 오버랩지도들)  
    const [directionStartPoint, setDirectionStartPoint] = useState<number[] | undefined>(undefined); // direction 시작점 [50]
    const [directionVehicle, setDirectionVehicle] = useState<string>(''); // direction 탈것 정보    
    const [mapProj, setMapProj] = useState<Projection>();
    const [isochronePolygon, setIsochronePolygon] = useState<Polygon | undefined>(undefined);
    const [accState, _setAccState] = useState<AccessibilityState>({ ready: false });
    const [resetAcc, setResetAcc] = useState<boolean>(false);

    // ScaleBar를 위한 Map 객체 상태
    const [currentMap, setCurrentMap] = useState<OlMap | undefined>(undefined);

    // 26.01.15 | dslee | Map 컴포넌트 ref (웹뷰 제어용)
    const mapWebViewRef = useRef<any>(null);

    // 25.07.07 | Kim Young Jun | 동일 매트릭 선택 시에도 분석 동작하도록 상태 추가
    //const [analysisTriggerId, setAnalysisTriggerId] = useState(0);    

    // 25.07.10 | Kim Young Jun | 단계구분도 활성화 여부 상태 추가
    const [isChoroplethAnalysis, setIsChoroplethAnalysis] = useState(false);
    const [currentRegionCode, setCurrentRegionCode] = useState<string | null>(null);

    // 경사도 분석 hook 사용
    const slopeHook = useSlopeAnalysis();

    // 장소조회 reload
    const setAccState = useCallback((vehicle?: TransportMode, ready?: boolean) => {
        if (!vehicle && ready === undefined) {
            _setAccState({ ready: false });
            return;
        }

        _setAccState((prev) => {
            const next = { ...(prev ?? { ready: false }) };
            if (vehicle) next.vehicle = vehicle;
            if (ready !== undefined) next.ready = ready;
            return next;
        });
    }, []);

    // Toast // !@삭제 불가 - 경사도 분석 줌 레벨 안내에 사용
    const [toast, setToast] = useState<ToastData | undefined>(); // [58]

    useEffect(() => {
        loadMap(baseMapID!);

        props.setIsMap(true);
        document.addEventListener('keydown', escEvent);

        return () => {
            props.setIsMap(false);
            document.removeEventListener('keydown', escEvent);
        };
    }, []);

    // 카테고리 관리
    const [isCategoriesReady, setIsCategoriesReady] = useState(false); // [60]

    const [webViewDirectionContext, setWebViewDirectionContext] = useState<{ duration: number, vehicle: string } | null>(null);
    const isSkipOpenPoi = useRef(false); // 2026-01-21 | dslee | RN 명령으로 인한 POI 변경 시 피드백 루프 방지용 플래그

    // 25.09.02 | Kim Young Jun | 현재 선택된 카테고리 setter 함수
    const setCurrentSelectedCategory = useCallback((value: React.SetStateAction<number>) => {
        setSelectedCategoryArray((prev) => {
            const next = [...prev];
            while (next.length <= selectedMapIdx) next.push(0);
            const current = next[selectedMapIdx] || 0;
            const categoryIndex = typeof value === 'function' ? value(current) : value;
            next[selectedMapIdx] = categoryIndex;
            return next;
        });
    }, [selectedMapIdx]);

    // dslee (2026.02.03) : 지도 새로고침 요청 핸들러
    const handleRefreshMap = useCallback(() => {
        if (!baseMapID) return;
        loadMap(baseMapID);
    }, [baseMapID]);

    // 2026-01-08 | dslee |  WebView 메시지 수신 핸들러
    const handleWebViewMessage = useCallback((message: WebViewMessageType, postMessage: (m: WebViewMessageType) => void) => {
        switch (message.type) {
            case 'ACTIVATE_POI': {
                const XID = Number(message.payload);
                isSkipOpenPoi.current = true;
                setSelectedPoiIdx(XID);
                break;
            }
            case 'CATEGORY_IDX': {
                const categoryIdx = Number(message.payload);
                setCurrentSelectedCategory(categoryIdx);
                break;
            }
            case 'DELETE_XID': {
                setSelectedPoiIdx(0);
                setEditPlaceStatus((prev) => !prev);
                break;
            }
            case 'SET_MARKER': {
                if (message.payload?.coordX && message.payload?.coordY) {
                    setIsAddMode(true);
                    mapWebViewRef.current?.showFixedMarker([message.payload.coordX, message.payload.coordY]);
                }
                break;
            }
            case 'SHOW_DRAGGABLE_MARKER':
                mapWebViewRef.current?.showDraggableMarker();
                break;
            case 'HIDE_DRAGGABLE_MARKER': {
                mapWebViewRef.current?.hideDraggableMarker();
                setIsAddMode(false);
                break;
            }

            case 'MAP_TOOL': {
                switch (message.payload) {
                    case 'Init': setActiveIdx(MapToolType.Init); break;
                    case 'Choropleth': setActiveIdx(MapToolType.Choropleth); break;
                    case 'Chart': setActiveIdx(MapToolType.Chart); break;
                    case 'Region': setActiveIdx(MapToolType.Region); break;
                    case 'Heat': setActiveIdx(MapToolType.Heat); break;
                    case 'Bubble': setActiveIdx(MapToolType.Bubble); break;
                    case 'Slope': setActiveIdx(MapToolType.Slope); break;
                    case 'Direction':
                        setWebViewDirectionContext(null);
                        setActiveIdx(MapToolType.Direction);
                        break;
                    case 'Accessibility': setActiveIdx(MapToolType.Accessibility); break;
                    case 'Distance': setActiveIdx(MapToolType.Distance); break;
                    case 'Area': setActiveIdx(MapToolType.Area); break;
                }
                break;
            }
            case 'HeatMapBlur':
                if (message.payload !== undefined && message.payload !== null) setBlur(Number(message.payload));
                break;
            case 'START_DIRECTION_ANALYSIS':
                if (message.payload) setWebViewDirectionContext(message.payload);
                break;
            case 'RESET_DIRECTION_ANALYSIS_MARKER':
                setClearByButton(0);
                setAnalysisXIDs(undefined);
                postMessage({ type: 'DIRECTION_MARKER_STATUS', payload: { hasMarker: [] } });
                postMessage({ type: 'DIRECTION_ANALYSIS_COMPLETED', payload: { isCompleted: false } });
                break;
            case 'START_ACCESSIBILITY_ANALYSIS':
                setAccState(TransportMode.car);
                break;
            case 'RESET_ACCESSIBILITY_ANALYSIS':
                setResetAcc(true);
                postMessage({ type: 'WEB_VIEW_MESSAGE' , payload: "분석 대상 지점을 선택해 주세요." });
                break;
            case 'REFRESH_MAP':
                setIsAddMode(false);
                handleRefreshMap();
                break;
            case 'CENTER_MY_LOCATION':
                if (message.payload?.latitude && message.payload?.longitude) {
                    mapWebViewRef.current?.centerToLocation(message.payload.latitude, message.payload.longitude);
                }
                break;
        }
    }, [setActiveIdx, setAccState, setCurrentSelectedCategory]);

    const { postMessage } = useWebViewBridge((message) => handleWebViewMessage(message, postMessage));

    // 마커 드래그 종료 시 WebView로 좌표 전송
    const handleMarkerDragEnd = useCallback(
        (coords: number[]) => {
            postMessage({
                type: 'MARKER_DRAGGED',
                payload: {
                    coordX: coords[0],
                    coordY: coords[1],
                },
            });
        },
        [postMessage]
    );

    // 2026-01-07 | dslee |  WebView 지로 로딩 완료 전송
    useEffect(() => {
        if (!isLoading && currentMap && window.ReactNativeWebView) {
            postMessage({ type: 'INIT' });
        }
    }, [isLoading, currentMap]);

    // 2026-01-07 | dslee |  WebView 선택된 POIIdx(장소 ID) 전송
    useEffect(() => {
        // 플래그가 true 이면 RN으로 다시 보내지 않고 종료
        if (isSkipOpenPoi.current) {
            isSkipOpenPoi.current = false; // 다음 사용자의 직접 클릭을 위해 초기화
            return;
        }
        postMessage({ type: 'OPEN_POI', payload: Number(selectedPoiIdx) });
    }, [selectedPoiIdx]);

    //  2026-01-19 | dslee | directionStartPoint 변경 시 RN으로 알림
    useEffect(() => {
        postMessage({ type: 'DIRECTION_MARKER_STATUS', payload: { hasMarker: directionStartPoint ?? [] } });
    }, [directionStartPoint, webViewDirectionContext]);


    // mapInfos 변경 시 카테고리 배열 업데이트
    useEffect(() => {
        const newCategoriesArray: CategoryOptions[][] = [];

        mapInfos.forEach((mapInfo, index) => {
            let categories = mapInfo.category || [];

            // 카테고리가 1개 이상이면 "전체" 옵션 추가
            if (categories.length > 0) {
                categories = [defaultCategory, ...categories];
            } else if (categories.length === 0) {
                // 카테고리가 없으면 기본 "전체" 옵션만
                categories = [defaultCategory];
            }

            newCategoriesArray[index] = categories;
        });

        // 카테고리 배열이 비어있으면 기본값 설정
        if (newCategoriesArray.length === 0) {
            newCategoriesArray[0] = [defaultCategory];
        }

        setCategoriesArray(newCategoriesArray);
    }, [mapInfos]);


    // 25.09.02 | Kim Young Jun | selectedCategoryArray만 사용하도록 헬퍼 함수들 추가
    const [selectedCategoryArray, setSelectedCategoryArray] = useState<number[]>([0]); // 기본 지도 카테고리 0으로 초기화

    // mapInfos 통합 관리를 위한 헬퍼 함수들 - useMemo로 최적화
    //현재 선택된 지도 정보 조회 함수 - mapInfos 배열의 현재 인덱스 요소 반환
    const getCurrentMapInfo = useMemo((): MapInfo | undefined => {
        return mapInfos[selectedMapIdx];
    }, [mapInfos, selectedMapIdx]);

    // 기준 지도 정보 조회 함수 - mapInfos 배열의 첫 번째 요소 반환
    const getBaseMapInfo = (): MapInfo | undefined => {
        return mapInfos[0];
    };

    const getOverlapMapInfos = (): MapInfo[] => {
        return mapInfos.slice(1);
    };

    // dslee (2026.01.29) : categoriesArray 의존성 추가
    const getCurrentCategories = useMemo<CategoryOptions[]>(() => {
        return categoriesArray[selectedMapIdx] ?? [{ label: '전체', disabled: false }]
    }, [selectedMapIdx, categoriesArray]);

    // 현재 선택된 지도의 카테고리 배열 getter 함수
    // const getCurrentCategories = (): CategoryOptions[] => {
    //     return categoriesArray[selectedMapIdx] || [{ label: '전체', disabled: false }];
    // };

    // 현재 선택된 지도의 카테고리 배열 setter 함수
    const setCurrentCategories = (categories: CategoryOptions[]) => {
        const newCategoriesArray = [...categoriesArray];
        while (newCategoriesArray.length <= selectedMapIdx) {
            newCategoriesArray.push([{ label: '전체', disabled: false }]);
        }
        newCategoriesArray[selectedMapIdx] = categories;
        setCategoriesArray(newCategoriesArray);
    };


    // 25.09.02 | Kim Young Jun | 지도 스위치 시 카테고리 변화 및 상태 복원 함수 개선
    useEffect(() => {
        let _categories: CategoryOptions[] = [];

        // 25.09.02 | Kim Young Jun | 배열 크기 확장이 필요한 경우
        const newArray = [...selectedCategoryArray];
        while (newArray.length <= selectedMapIdx) {
            newArray.push(0);
        }

        // 이전 지도의 현재 선택 상태를 저장하는 로직 개선
        const currentIndex = selectedMapIdx;
        const prevIndex = prevSelectedCategoryRef.current;

        // 이전 선택 인덱스가 유효하고 현재와 다른 경우에만 저장
        if (prevIndex !== null && prevIndex !== currentIndex && prevIndex < newArray.length) {
            // 25.09.02 | Kim Young Jun | 이전 지도의 현재 카테고리 상태를 저장
            const prevCategory = selectedCategoryArray[prevIndex] || 0;
            newArray[prevIndex] = prevCategory;
        }

        // 현재 선택된 인덱스를 이전 값으로 저장
        prevSelectedCategoryRef.current = selectedMapIdx;

        // 25.09.02 | Kim Young Jun | FloatingSideSheet가 열려있지 않을 때만 선택된 마커 초기화
        if (selectedPoiIdx === 0) {
            setSelectedPoiIdx(0); // 선택된 마커 초기화
        }

        // 카테고리 정보 가져오기
        if (selectedMapIdx === 0) {
            const baseMapInfo = getBaseMapInfo();
            if (!baseMapInfo) return;
            _categories = baseMapInfo.category;
        } else {
            const overlapMaps = getOverlapMapInfos();
            if (!overlapMaps[selectedMapIdx - 1]) return;
            _categories = overlapMaps[selectedMapIdx - 1].category;
        }

        if (_categories.length > 0) {
            _categories = [defaultCategory, ..._categories];
        }

        // 25.09.02 | Kim Young Jun | 전환할 지도의 저장된 카테고리 상태 복원 및 범위 체크
        const savedCategory = newArray[selectedMapIdx] || 0;

        // 저장된 카테고리가 새 카테고리 범위를 벗어나면 0으로 리셋
        if (savedCategory >= _categories.length) {
            newArray[selectedMapIdx] = 0;
        }

        // 25.09.02 | Kim Young Jun | 카테고리 설정과 배열 업데이트를 동시에 수행
        if (_categories.length) {
            // 상태 업데이트를 하나의 배치로 처리
            setCurrentCategories(_categories);

            // selectedCategoryArray 업데이트 (React 18의 배치 업데이트로 함께 처리됨)
            if (JSON.stringify(newArray) !== JSON.stringify(selectedCategoryArray)) {
                setSelectedCategoryArray(newArray);
            }
        }

    }, [selectedMapIdx, mapInfos.length]);


    // 분석 결과 XID 변경 감지 - 분석 결과 목록 조회 및 업데이트
    useEffect(() => {
        if (analysisXIDs && analysisXIDs.length > 0) {
            if (
                activeIdx === MapToolType.Overlap ||
                activeIdx === MapToolType.Direction ||
                activeIdx === MapToolType.Accessibility ||
                activeIdx === MapToolType.Add
            )
                return;
        }
    }, [analysisXIDs, activeIdx]);

    // 접근성 분석 관련
    useEffect(() => {
        // 접근성 분석 초기화 시 실행함수
        _setAccState({ ready: false });
    }, [resetAcc]);

    const [agreeNoneAddress, setAgreeNoneAddress] = useState<boolean>(false);

    // 25.07.11 | Kim Young Jun | isAnalysisActive 분석상태 체크 callback 함수 정의
    const isAnalysisActive = useMemo(() => {
        return [
            MapToolType.Region,
            MapToolType.Choropleth,
            MapToolType.Chart,
            MapToolType.Bubble,
            MapToolType.Heat,
            MapToolType.Direction,
            MapToolType.Accessibility,
            MapToolType.ExtentAnalysis,
            MapToolType.Overlap,
            MapToolType.Slope,
        ].includes(activeIdx);
    }, [activeIdx]);

    const isNotUseMarkerAnalysis = useMemo(() => {
        return [MapToolType.Region, MapToolType.Choropleth, MapToolType.Chart, MapToolType.Bubble, MapToolType.Heat].includes(activeIdx);
    }, [activeIdx]);

    // 25.09.02 | Kim Young Jun | 오버랩 기능 활성화 상태 체크 함수 수정 - 통합된 상태 관리
    const isOverlapActive = useMemo(() => {
        return params.get('overlap') !== null && mapInfos.length > 1;
    }, [params, mapInfos.length]);

    // 25.07.18 | Kim Young Jun | 분석도구 이전 상태 추적
    const [prevAnalysisState, setPrevAnalysisState] = useState(false);
    const isFirstRender = useRef(true);

    // 분석도구 상태 변화 감지
    useEffect(() => {
        const currentAnalysisState = isAnalysisActive;

        if (!isFirstRender.current) {
            setPrevAnalysisState(!currentAnalysisState);
        }
        else {
            // 최초 렌더링 후 플래그 업데이트
            isFirstRender.current = false;
        }
    }, [isAnalysisActive, prevAnalysisState]);

    // 25.07.07 | Kim Young Jun | Analysis Metric 트리거 함수 정의
    // !@ gsshin (2026.01.27) - 사용안함
    // const triggerAnalysis = useCallback(() => {
    //     setAnalysisTriggerId((prev) => {
    //         return prev + 1;
    //     });
    // }, []);

    // 25.08.04 | Kim Seong Min | 경사도 표출을 위한 최소 줌 레벨 변수 정의
    const minSlopeZoomLv = 10;

    // move page
    const movePage = (url?: To, option?: NavigateOptions) => {
        navigate(url ?? path.PUBLIC_MAPLIST, option);
    };

    // request data -------------------------
    const getMapInfo = async (mapid: string) => {
        return await DataProvider.GetMapInfo({ mapid });
    };

    const loadMap = async (mapid: string) => {
        // update view count
        await DataProvider.UpdateMapViewCnt({ mapid });

        // request Map Info
        const response = await getMapInfo(mapid);

        if (response.flag && response.result) {
            // 기본 지도 정보를 mapInfos 배열의 첫 번째 요소로 설정
            const baseMapInfo = response.result;
            const overlapMaps = getOverlapMapInfos();
            setMapInfos([baseMapInfo, ...overlapMaps]);
        } else {
            // 존재하지 않는 지도인 경우 목록 화면으로 이동
            if (response.status && response.status === 404) {
                movePage(undefined, { replace: true });
            }
            return; // response.result가 없으면 여기서 종료
        }

        let category = (response.result?.category as CategoryOptions[]) || [];

        if (category.length >= 1) {
            // console.log("@@ [MapScreen] setCurrentCategories category.length > 1: ", category);
            setCurrentCategories([defaultCategory, ...category]);
        } else if (category.length === 1) {
            // console.log("@@ [MapScreen] setCurrentCategories category.length === 1: ", category);
            setCurrentCategories([...category]);
        }

        setIsCategoriesReady(true);
    };


    /**
     * Direction 함수
     */
    const finishDirectionAnalysis = (xidList: number[], polygon: Polygon) => {
        // 1. xid list로 contents 호출 _ 되도록 State set
        setAnalysisXIDs(xidList);

        // 3. state에 Polygon 저장해서 map으로 넘겨준다(이후 map에서 지도에 폴리곤 그려줌)
        setIsochronePolygon(polygon);
    };

    // 확장 툴 셀렉터 인덱스 State, set하는데가 없어서 상수 처리 한다.
    const analysisMetricsToolIdx = 21;
    const extentToolIdx = 25;

    // 사이드 시트 컨텐츠 영역 -----------------------------------------------
    const renderContent = (type: ContentList) => {
        if (type === ContentList.Direction) {
            return (
                <DirectionAnalysis
                    changeContent={() => { }}// changeContent}
                    startPoint={directionStartPoint}
                    setStartPoint={setDirectionStartPoint}
                    mapProj={mapProj}
                    mapId={baseMapID}
                    finishAnalysis={finishDirectionAnalysis}
                    setAnalysisStage={setAnalysisStage}
                    setClearByButton={setClearByButton}
                    setDirectionVehicle={setDirectionVehicle}
                    setIsLoading={setIsLoading} // 25.07.03 | Kim Young Jun | 로딩 상태 설정
                    webViewDirectionContext={webViewDirectionContext} // 26.01.19 | dslee | 웹뷰 데이터 셋
                    postMessage={postMessage}
                />
            );
        } else if (type === ContentList.Accessibility) {
            return <AccessibilityAnalysis changeContent={() => { }} accState={accState} setAccState={setAccState} />;// changeContent}
        }

        return undefined;
    };

    const escEvent = (e: KeyboardEvent) => {
        const currentIdx = activeIdxRef.current ?? -1;
        const measureTools = [MapToolType.Distance, MapToolType.Area, MapToolType.Radius];

        if (measureTools.includes(currentIdx)) {
            e.key.toLowerCase() === 'escape' && setActiveIdx(MapToolType.None);
        }
    };


    return (
        <>
            <div className={styles.wrapper}>
                {/* <div className={styles.left_container} ref={sideRef}></div> */}
                <div className={styles.right_container}>

                    {currentMap && <ScaleBar map={currentMap} />}
                </div>

                {isCategoriesReady && getCurrentMapInfo && (
                    <MobileMap
                        ref={mapWebViewRef} // dslee | 26.01.15 | 웹뷰 제어 ref 추가
                        onMarkerDragEnd={handleMarkerDragEnd} // dslee | 26.01.15 | 웹뷰 제어 마커 제거 추가
                        onRefreshRequested={handleRefreshMap} // dslee (2026.02.03) : 지도 새로고침 요청 콜백
                        postMessage={postMessage} // 2026.01.19 | dslee | 웹뷰 postMessage
                        selectedPoiIdx={selectedPoiIdx}
                        setSelectedPoiIdx={setSelectedPoiIdx}
                        categories={getCurrentCategories}
                        selectedCategoryArray={selectedCategoryArray}
                        activeIdx={activeIdx}
                        setActiveIdx={setActiveIdx}
                        setAnalysisXIDs={setAnalysisXIDs}
                        zoom={{
                            zoom: zoomLv,
                            setZoom: setZoomLv,
                        }}
                        isGPSOn={isGPSOn}
                        setIsGPSOn={setIsGPSOn}
                        isAddMode={isAddMode}
                        //mapPadding={0}
                        newPlaces={newPlaceCoords}
                        setNewPlace={setNewPlaceCoords}
                        //allPlaceData={[]}
                        blur={blur}
                        clearByButton={clearByButton}
                        setClearByButton={setClearByButton}
                        isExtentAnalysis={isExtentAnalysis}
                        setIsExtentAnalysis={setIsExtentAnalysis}
                        analysisStage={analysisStage}
                        setAnalysisStage={setAnalysisStage}
                        setMapProj={setMapProj}
                        startPoint={directionStartPoint} // 시간별 이동거리 시작지점
                        setDirectionStartPoint={setDirectionStartPoint}
                        directionVehicle={directionVehicle}
                        isochronePolygon={isochronePolygon}
                        accState={accState}
                        setAccState={setAccState}
                        famousMarkers={getCurrentMapInfo?.famousMarker ?? undefined}
                        //movePage={movePage}
                        // 25.09.02 | Kim Young Jun | upadateSelectedCategory 대신 setCurrentSelectedCategory 사용
                        upadateSelectedCategory={(idx?: number) => {
                            if (idx !== undefined) {
                                // idx가 지정된 경우 해당 지도의 카테고리를 현재 선택된 카테고리로 설정
                                const newArray = [...selectedCategoryArray];
                                while (newArray.length <= idx) {
                                    newArray.push(0);
                                }
                                newArray[idx] = selectedCategoryArray[selectedMapIdx] || 0;
                                setSelectedCategoryArray(newArray);
                            }
                        }}

                        resetAcc={resetAcc}
                        setResetAcc={setResetAcc}
                        setToast={setToast}
                        analysisMetricsToolIdx={analysisMetricsToolIdx}
                        extentToolIdx={extentToolIdx}

                        //selectedDropdownField={null}

                        //analysisTriggerId={analysisTriggerId}
                        //triggerAnalysis={triggerAnalysis}
                        prevAnalysisState={prevAnalysisState}
                        isAnalysisActive={isAnalysisActive}
                        isNotUseMarkerAnalysis={isNotUseMarkerAnalysis}
                        // 25.07.10 | Kim Young Jun | 단계분석, 현재 선택한 행정구역 정보 상태 추가
                        isChoroplethAnalysis={isChoroplethAnalysis}
                        setIsChoroplethAnalysis={setIsChoroplethAnalysis}
                        currentRegionCode={currentRegionCode}
                        setCurrentRegionCode={setCurrentRegionCode}
                        setIsLoading={setIsLoading}
                        editPlaceStatus={editPlaceStatus}
                        setEditPlaceStatus={setEditPlaceStatus}
                        //prevSelectedIdxRef={prevSelectedIdxRef}
                        setNewPlaceAddress={setNewPlaceAddress}
                        //setIsOpenAgreeModal={() => { }}
                        newPlaceAddress={newPlaceAddress}
                        //setRevGeoLoading={() => { }}
                        setAgreeNoneAddress={setAgreeNoneAddress}
                        searchOptions={searchOptions}
                        setSearchOptions={setSearchOptions}
                        agreeNoneAddress={agreeNoneAddress}
                        setLoadingMessage={setLoadingMessage}
                        minSlopeZoomLv={minSlopeZoomLv}
                        mapInfos={mapInfos}
                        selectedMapIdx={selectedMapIdx || 0}
                        setSelectedMapIdx={setSelectedMapIdx}
                        slopeHook={slopeHook}
                        onMapReady={setCurrentMap}
                    />
                )}

                {/* 2026.01.19 | dslee | WebView DirectionAnalysis 활성화 */}
                {activeIdx === MapToolType.Direction && (
                    <div style={{ display: 'none' }}>
                        {renderContent(ContentList.Direction)}
                    </div>
                )}

                {activeIdx === MapToolType.Accessibility && (
                    <div style={{ display: 'none' }}>
                        {renderContent(ContentList.Accessibility)}
                    </div>
                )}

                {toast && createPortal(<Toast data={toast} setToast={setToast} />, document.body)}
                {/* 25.09.09 | 경사도 분석 활성화 시 줌 레벨 안내 토스트 */}
                {((activeIdx === MapToolType.Slope && !isOverlapActive) || (isOverlapActive && slopeHook.isSlopeActive)) &&
                    zoomLv < minSlopeZoomLv &&
                    createPortal(
                        <div style={{ pointerEvents: "none" }} className={styles.slopeToast} >
                            <span>경사 및 고도정보를 보기 위해서 지도를 좀 더 확대해 주세요</span>
                        </div>,
                        document.body
                    )}
                {/* {geocodingState > -1 && geocodingState < 101 && renderProgressBar()} */}
                <LoadingCoverLayer isLoading={isLoading} message={loadingMessage} />
            </div>
        </>
    );
};

export default MobileMapScreen;
